import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

import Waveform from 'js/src/components/app/waveform/Waveform';
import type { WaveformData } from 'js/src/components/app/waveform/waveform-types';
import { noop } from 'js/src/libs/consts';

import { downsampleVolumes, MAX_WAVEFORM_POINTS, streamWaveformVolumes } from './waveform-volumes';

/**
 * Width of individual bar at max zoom level
 */
const BAR_MAX_WIDTH_PIXELS = 2;
const NO_WAVEFORM_DATA: WaveformData = [];
/**
 * Decoding a file in one go holds the whole download plus every decoded audio frame in memory
 * (about 4GB for a 1hr webinar), which crashes the tab. Past this size, go without a waveform.
 */
const MAX_WHOLE_FILE_DECODE_BYTES = 150 * 1024 * 1024;
const TOO_LARGE_TO_DECODE = Symbol('too large to decode');

export type Props = {
    source: string;
    onLoaded: VoidFunction;
    onAudioDetected: (hasAudio: boolean) => void;
    className?: string;
};

const TrimmerAudioTimeline = ({ source, onLoaded, onAudioDetected, className }: Props) => {
    const [waveformDataFetched, setWaveformDataFetched] = useState(false);
    const [hasAudio, setHasAudio] = useState(false);
    const [originalWaveformData, setOriginalWaveformData] = useState<Float32Array>(
        new Float32Array(),
    );
    const [renderWaveformData, setRenderWaveformData] =
        React.useState<WaveformData>(NO_WAVEFORM_DATA);

    const handleWaveformResize = useCallback(
        (width: number) => {
            if (width <= 0 || originalWaveformData.length === 0) return;
            const numBars = Math.max(1, Math.floor(width / BAR_MAX_WIDTH_PIXELS));
            const bars = downsampleVolumes(originalWaveformData, numBars);
            setRenderWaveformData(Array.from(bars));
        },
        [originalWaveformData],
    );

    useEffect(() => {
        const abortController = new AbortController();
        const decodeWholeFile = async () => {
            const response = await fetch(source, { signal: abortController.signal });
            if (Number(response.headers?.get('content-length')) > MAX_WHOLE_FILE_DECODE_BYTES) {
                abortController.abort();
                return TOO_LARGE_TO_DECODE;
            }
            const arrayBuffer = await response.arrayBuffer();
            const audioContext = new AudioContext();
            try {
                const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
                const rawAudioData = audioBuffer.getChannelData(0); // We only need to work with one channel of data
                return downsampleVolumes(
                    rawAudioData,
                    Math.min(MAX_WAVEFORM_POINTS, rawAudioData.length),
                );
            } finally {
                audioContext.close?.().catch(noop);
            }
        };
        const showVolumes = (volumes: Float32Array) => {
            setOriginalWaveformData(volumes);
            setHasAudio(volumes.length > 0);
        };
        const computeAndStoreWaveformData = async () => {
            try {
                const streamed = await streamWaveformVolumes(source, abortController.signal);
                if (abortController.signal.aborted) {
                    return;
                }
                if (streamed.volumes) {
                    showVolumes(streamed.volumes);
                    return;
                }
                const decoded = await decodeWholeFile();
                if (decoded === TOO_LARGE_TO_DECODE) {
                    // Unmeasured is not silent: keep the mute control unless we know there's no audio
                    setHasAudio(streamed.hasAudioTrack ?? true);
                    return;
                }
                if (!abortController.signal.aborted) {
                    showVolumes(decoded);
                }
            } catch (e) {
                /**
                 * Possible reasons for error:
                 * - AudioContext is not supported
                 * - Fetch request was aborted
                 * - Fetch request failed
                 * - Decoding audio data failed
                 *
                 * In all these cases, we just want to ignore the error and not do anything.
                 * The waveform will not be displayed in the UI.
                 **/
            } finally {
                setWaveformDataFetched(true);
            }
        };
        computeAndStoreWaveformData().catch(noop);
        return () => {
            /**
             * Abort any pending fetch requests when
             * the component unmounts or re-renders
             */
            abortController.abort();
        };
    }, [source]);

    useEffect(() => {
        if (waveformDataFetched && onLoaded) {
            onAudioDetected(hasAudio);
            onLoaded();
        }
    }, [onAudioDetected, onLoaded, hasAudio, waveformDataFetched]);

    return (
        <div className={classNames('trimmer-audio-timeline', className)}>
            <Waveform
                waveformData={renderWaveformData}
                fillPercentage={0}
                onResize={handleWaveformResize}
            />
        </div>
    );
};

export default TrimmerAudioTimeline;
